import { Injectable } from '@angular/core';
import { EChartsOption, ECharts } from 'echarts';
import { Observable, Subject } from 'rxjs';
import { ClimateChart, ChartConfig, YAxis } from '../../../model/climate/climate-chart';
import { getSSPColor } from './custom-theme';
import { ClimateChartHelper } from './util/climate-chart-helper';

@Injectable()
export class ClimateTrChangeService {
  private static readonly DEBOUNCE_DELAY = 0;

  private readonly locationChartSubject: Subject<ECharts>;
  private readonly addressChartSubject: Subject<ECharts>;

  constructor() {
    this.locationChartSubject = new Subject<ECharts>();
    this.addressChartSubject = new Subject<ECharts>();
  }

  emitLocationChartEvent(chartInstance: ECharts): void {
    this.locationChartSubject.next(chartInstance);
  }

  getLocationChartSubject(): Observable<ECharts> {
    return this.locationChartSubject.asObservable();
  }

  emitAddressChartEvent(chartInstance: ECharts): void {
    this.addressChartSubject.next(chartInstance);
  }

  getAddressChartSubject(): Observable<ECharts> {
    return this.addressChartSubject.asObservable();
  }

  options: EChartsOption = {
    animation: false,
    animationDuration: 0,
    animationDurationUpdate: 0,
    height: '80%',
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          backgroundColor: '#6a7985'
        }
      },
      valueFormatter: (value: number | string) => ClimateChartHelper.formatChartValue(value)
    },
    legend: { top: 10 },
    toolbox: ClimateChartHelper.getToolboxConfig('Change in Tropical Nights', 'Climate_TR_Change'),
    dataZoom: {
      show: false,
      start: 0,
      end: 100
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: ClimateChartHelper.getDualXAxisConfig({
      type: 'category',
      boundaryGap: ['10%', '10%'],
      axisLabel: { interval: 0 }
    }),
    yAxis: [
      {
        type: 'value',
        scale: true,
        name: 'days',
        position: 'left',
        alignTicks: true,
        axisLine: {
          show: true,
        },
        axisLabel: {
          formatter: '{value}'
        },
        boundaryGap: [0.2, 0.2]
      }
    ],
    series: [],
    dataset: {}
  };

  updateClimateTrChangeChart(echartsInstance: ECharts, climateChart: ClimateChart) {
    const chartConfig: ChartConfig = climateChart.chartConfig;
    const yAxis: YAxis = chartConfig.yAxis;
    const seriesData = [];
    const source = [];

    source.push(climateChart.xAxis);

    for (const [key, value] of Object.entries(climateChart.series)) {
      seriesData.push(ClimateChartHelper.getSeriesConfig(key, chartConfig.type, getSSPColor, true));
      source.push(ClimateChartHelper.padSSPScenarioData(key, value, climateChart.xAxis));
    }
    const bounds = ClimateChartHelper.calculateAxisBounds(
      climateChart.series, 
      yAxis.min, 
      yAxis.max, 
      yAxis.interval
    );
    echartsInstance.setOption({
      animation: false,
      animationDuration: 0,
      animationDurationUpdate: 0,
      xAxis: ClimateChartHelper.getDualXAxisConfig({
        type: 'category',
        boundaryGap: ['10%', '10%'],
        axisLabel: { interval: 0 }
      }),
      yAxis: [
        {
          type: 'value',
          scale: true,
          name: yAxis.title || 'days',
          min: bounds.min,
          max: bounds.max,
          interval: bounds.interval,
          position: 'left',
          alignTicks: true,
          axisLine: {
            show: true,
          },
          axisLabel: {
            formatter: '{value} '
          },
          boundaryGap: [0.2, 0.2]
        }
      ],
      dataset: {
        source: source
      },
      series: seriesData
    });
    
    ClimateChartHelper.attachDownloadHandler(echartsInstance, 'Tropical Nights - Number of days with minimum temperature above 20°C (TR)', 'Tropical_Nights_TR_Change');
    if (echartsInstance && typeof echartsInstance.on === 'function') {
      let applying = false;
      try { 
        echartsInstance.off && echartsInstance.off('finished'); 
      } catch (error) {
        console.error('Error removing finished event listener:', error);
      }
      echartsInstance.on('finished', () => {
        if (applying) return;
        applying = true;
        echartsInstance.setOption({
          xAxis: ClimateChartHelper.getDualXAxisConfig({
            type: 'category',
            boundaryGap: ['10%', '10%'],
            axisLabel: { interval: 0 }
          })
        }, { replaceMerge: ['xAxis'] });
        setTimeout(() => { applying = false; }, ClimateTrChangeService.DEBOUNCE_DELAY);
      });
    }
  }
}
